/**
 * Studio's checkout-page preview — renders the buyer page using the same
 * markup vocabulary the production renderer uses (`.section`, `.method`,
 * `.pay-btn`, etc.). The matching styles live in preview.css, scoped under
 * `.cp-preview-frame` so they don't leak into the rest of the Studio UI.
 *
 * Whenever the buyer-frontend stylesheet changes, mirror the change in
 * preview.css to keep the WYSIWYG promise.
 */

/* eslint-disable react-refresh/only-export-components */
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import type {
  BlockId,
  CheckoutPageNode,
  Device,
  LayoutBlockConfig,
  LayoutBlocks,
  SectionName,
} from "../../types/flow";
import { loadCustomHtml } from "../../lib/customHtmlStorage";
import {
  DEFAULT_PREVIEW_EVAL_CTX,
  evaluateVisibility,
} from "../builder/ruleEngine";
import "./preview.css";

export type WalletPreviewMode =
  | "design"
  | "ios_safari"
  | "mac_safari"
  | "chrome_desktop"
  | "android_chrome"
  | "unavailable";

export interface BuyerCtx {
  cart: {
    items: { sku: string; name: string; qty: number; price_cents: number }[];
    currency: string;
    subtotal_cents: number;
    tax_cents: number;
    shipping_cents: number;
  };
  blockConfig: LayoutBlockConfig;
  buyer: { email?: string; phone?: string };
}

export const DEMO_CTX: BuyerCtx = {
  cart: {
    items: [
      { sku: "HOODIE-M", name: "Blue Hoodie (M)", qty: 2, price_cents: 4900 },
      { sku: "CAP-ONESIZE", name: "Cap (one size)", qty: 1, price_cents: 1900 },
    ],
    currency: "EUR",
    subtotal_cents: 11700,
    tax_cents: 2340,
    shipping_cents: 500,
  },
  blockConfig: {},
  buyer: {},
};

// ─── Brand-icon catalog (mirrors frontend/src/render/icons.ts) ───────────────

function iconsForMethod(id: string): string[] {
  switch (id) {
    case "credit-card": return ["visa", "mastercard", "amex"];
    case "apple_pay": return ["apple_pay"];
    case "google_pay": return ["google_pay"];
    case "swish": return ["swish"];
    case "vipps": return ["vipps"];
    case "mobilepay": return ["mobilepay"];
    case "vipps_mobilepay": return ["vipps_mobilepay"];
    default: return [];
  }
}

// Vendored from activemerchant/payment_icons (MIT). Mirror of
// frontend/src/render/brandSvgs.ts — keep in sync when changed.
export const BRAND_SVG: Record<string, string> = {
  visa: `<svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" width="38" height="24" aria-label="Visa"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path d="M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2zm-13.4-.3l.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2M5 8.2c0-.1.2-.2.3-.2h3.4c.5 0 .9.3 1 .8l.9 4.4c0 .1 0 .1.1.2 0-.1.1-.1.1-.1l2.1-5.1c-.1-.1 0-.2.1-.2h2.1c0 .1 0 .1-.1.2l-3.1 7.3c-.1.2-.1.3-.2.4-.1.1-.3 0-.5 0H9.7c-.1 0-.2 0-.2-.2L7.9 9.5c-.2-.2-.5-.5-.9-.6-.6-.3-1.7-.5-1.9-.5L5 8.2z" fill="#142688"/></svg>`,
  mastercard: `<svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" width="38" height="24" aria-label="Mastercard"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><circle fill="#EB001B" cx="15" cy="12" r="7"/><circle fill="#F79E1B" cx="23" cy="12" r="7"/><path fill="#FF5F00" d="M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7s1.2 4.5 3 5.7c1.8-1.2 3-3.3 3-5.7z"/></svg>`,
  maestro: `<svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" width="38" height="24" aria-label="Maestro"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><circle fill="#0099DF" cx="15" cy="12" r="7"/><circle fill="#ED0006" cx="23" cy="12" r="7"/><path fill="#6C6BBD" d="M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7s1.2 4.5 3 5.7c1.8-1.2 3-3.3 3-5.7z"/></svg>`,
  electron: `<svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" width="38" height="24" aria-label="Visa Electron"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#142688" d="M13.6 8.4h2.2l-1.4 7.2h-2.2l1.4-7.2zm6.3 0-2.1 4.9-.2-1.1-.7-3.4c-.1-.4-.3-.5-.6-.5H13l-.1.2c.6.1 1.2.3 1.7.6l1.3 6h2.3l3.5-6.7h-2.3zm8.6 0h-2c-.3 0-.5.1-.6.4l-3 6.8h2.1l.4-1.2h2.5l.2 1.2h1.9l-1.5-7.2zm-2.5 4.5.8-2.4.5 2.4h-1.3z"/><path fill="#F7981D" d="M22.4 10.7c0-.9.8-1.5 2.6-1.5 1.1 0 1.9.2 2.4.5l.3-1.6c-.5-.3-1.4-.5-2.4-.5-2.7 0-4.5 1.2-4.5 3 0 1.2.9 1.9 2 2.3 1.1.5 1.4.7 1.4 1.1 0 .6-.7.9-1.7.9s-2-.3-2.6-.6l-.3 1.7c.5.3 1.6.5 2.8.5 2.8 0 4.6-1.2 4.6-3.1 0-2.3-3.6-2.1-3.6-3.2z"/><path fill="#F7981D" d="M5.5 9H3v.3c2.2.5 3.7 1.8 4.3 3.3l-.6-3c-.1-.4-.3-.6-.7-.6h-.5z"/></svg>`,
  dankort: `<svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" role="img" width="38" height="24" aria-label="Dankort"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#ED1C24" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#fff" d="M3 10.5h13V13H3zM18.5 3v18H21V3z"/><text x="26" y="15" font-family="'Helvetica Neue',Arial,sans-serif" font-size="7" font-weight="700" fill="#fff">DK</text></svg>`,
  amex: `<svg xmlns="http://www.w3.org/2000/svg" role="img" aria-label="American Express" viewBox="0 0 38 24" width="38" height="24"><path fill="#000" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3Z" opacity=".07"/><path fill="#006FCF" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32Z"/><path fill="#FFF" d="M22.012 19.936v-8.421L37 11.528v2.326l-1.732 1.852L37 17.573v2.375h-2.766l-1.47-1.622-1.46 1.628-9.292-.02Z"/><path fill="#006FCF" d="M23.013 19.012v-6.57h5.572v1.513h-3.768v1.028h3.678v1.488h-3.678v1.01h3.768v1.531h-5.572Z"/><path fill="#006FCF" d="m28.557 19.012 3.083-3.289-3.083-3.282h2.386l1.884 2.083 1.89-2.082H37v.051l-3.017 3.23L37 18.92v.093h-2.307l-1.917-2.103-1.898 2.104h-2.321Z"/><path fill="#FFF" d="M22.71 4.04h3.614l1.269 2.881V4.04h4.46l.77 2.159.771-2.159H37v8.421H19l3.71-8.421Z"/><path fill="#006FCF" d="m23.395 4.955-2.916 6.566h2l.55-1.315h2.98l.55 1.315h2.05l-2.904-6.566h-2.31Zm.25 3.777.875-2.09.873 2.09h-1.748Z"/><path fill="#006FCF" d="M28.581 11.52V4.953l2.811.01L32.84 9l1.456-4.046H37v6.565l-1.74.016v-4.51l-1.644 4.494h-1.59L30.35 7.01v4.51h-1.768Z"/></svg>`,
  apple_pay: `<svg version="1.1" xmlns="http://www.w3.org/2000/svg" role="img" width="38" height="24" viewBox="0 0 165.521 105.965" aria-label="Apple Pay"><path fill="#000" d="M150.698 0H14.823c-.566 0-1.133 0-1.698.003-.477.004-.953.009-1.43.022-1.039.028-2.087.09-3.113.274a10.51 10.51 0 0 0-2.958.975 9.932 9.932 0 0 0-4.35 4.35 10.463 10.463 0 0 0-.975 2.96C.113 9.611.052 10.658.024 11.696a70.22 70.22 0 0 0-.022 1.43C0 13.69 0 14.256 0 14.823v76.318c0 .567 0 1.132.002 1.699.003.476.009.953.022 1.43.028 1.036.09 2.084.275 3.11a10.46 10.46 0 0 0 .974 2.96 9.897 9.897 0 0 0 1.83 2.52 9.874 9.874 0 0 0 2.52 1.83c.947.483 1.917.79 2.96.977 1.025.183 2.073.245 3.112.273.477.011.953.017 1.43.02.565.004 1.132.004 1.698.004h135.875c.565 0 1.132 0 1.697-.004.476-.002.952-.009 1.431-.02 1.037-.028 2.085-.09 3.113-.273a10.478 10.478 0 0 0 2.958-.977 9.955 9.955 0 0 0 4.35-4.35c.483-.947.789-1.917.974-2.96.186-1.026.246-2.074.274-3.11.013-.477.02-.954.022-1.43.004-.567.004-1.132.004-1.699V14.824c0-.567 0-1.133-.004-1.699a63.067 63.067 0 0 0-.022-1.429c-.028-1.038-.088-2.085-.274-3.112a10.4 10.4 0 0 0-.974-2.96 9.94 9.94 0 0 0-4.35-4.35A10.52 10.52 0 0 0 156.939.3c-1.028-.185-2.076-.246-3.113-.274a71.417 71.417 0 0 0-1.431-.022C151.83 0 151.263 0 150.698 0z"/><path fill="#FFF" d="M150.698 3.532l1.672.003c.452.003.905.008 1.36.02.793.022 1.719.065 2.583.22.75.135 1.38.34 1.984.648a6.392 6.392 0 0 1 2.804 2.807c.306.6.51 1.226.645 1.983.154.854.197 1.783.218 2.58.013.45.019.9.02 1.36.005.557.005 1.113.005 1.671v76.318c0 .558 0 1.114-.004 1.682-.002.45-.008.9-.02 1.35-.022.796-.065 1.725-.221 2.589a6.855 6.855 0 0 1-.645 1.975 6.397 6.397 0 0 1-2.808 2.807c-.6.306-1.228.511-1.971.645-.881.157-1.847.2-2.574.22-.457.01-.912.017-1.379.019-.555.004-1.113.004-1.669.004H14.801c-.55 0-1.1 0-1.66-.004a74.993 74.993 0 0 1-1.35-.018c-.744-.02-1.71-.064-2.584-.22a6.938 6.938 0 0 1-1.986-.65 6.337 6.337 0 0 1-1.622-1.18 6.355 6.355 0 0 1-1.178-1.623 6.935 6.935 0 0 1-.646-1.985c-.156-.863-.2-1.788-.22-2.578a66.088 66.088 0 0 1-.02-1.355l-.003-1.327V14.474l.002-1.325a66.7 66.7 0 0 1 .02-1.357c.022-.792.065-1.717.222-2.587a6.924 6.924 0 0 1 .646-1.981c.304-.598.7-1.144 1.18-1.623a6.386 6.386 0 0 1 1.624-1.18 6.96 6.96 0 0 1 1.98-.646c.865-.155 1.792-.198 2.586-.22.452-.012.905-.017 1.354-.02l1.677-.003h135.875"/><path fill="#000" d="M43.508 35.77c1.404-1.755 2.356-4.112 2.105-6.52-2.054.102-4.56 1.355-6.012 3.112-1.303 1.504-2.456 3.959-2.156 6.266 2.306.2 4.61-1.152 6.063-2.858"/><path fill="#000" d="M45.587 39.079c-3.35-.2-6.196 1.9-7.795 1.9-1.6 0-4.049-1.8-6.698-1.751-3.447.05-6.645 2-8.395 5.1-3.598 6.2-.95 15.4 2.55 20.45 1.699 2.5 3.747 5.25 6.445 5.151 2.55-.1 3.549-1.65 6.647-1.65 3.097 0 3.997 1.65 6.696 1.6 2.798-.05 4.548-2.5 6.247-5 1.95-2.85 2.747-5.6 2.797-5.75-.05-.05-5.396-2.101-5.446-8.251-.05-5.15 4.198-7.6 4.398-7.751-2.399-3.548-6.147-3.948-7.447-4.048"/><path fill="#000" d="M78.973 32.11c7.278 0 12.347 5.017 12.347 12.321 0 7.33-5.173 12.373-12.529 12.373h-8.058V69.62h-5.822V32.11h14.062zm-8.24 19.807h6.68c5.07 0 7.954-2.729 7.954-7.46 0-4.73-2.885-7.434-7.928-7.434h-6.706v14.894z"/><path fill="#000" d="M92.764 61.847c0-4.809 3.665-7.564 10.423-7.98l7.252-.442v-2.08c0-3.04-2.001-4.704-5.562-4.704-2.938 0-5.07 1.507-5.51 3.82h-5.252c.157-4.86 4.731-8.395 10.918-8.395 6.654 0 10.995 3.483 10.995 8.89v18.663h-5.38v-4.497h-.13c-1.534 2.937-4.914 4.782-8.579 4.782-5.406 0-9.175-3.222-9.175-8.057zm17.675-2.417v-2.106l-6.472.416c-3.64.234-5.536 1.585-5.536 3.95 0 2.288 1.975 3.77 5.068 3.77 3.95 0 6.94-2.522 6.94-6.03z"/><path fill="#000" d="M120.975 79.652v-4.496c.364.051 1.247.103 1.715.103 2.573 0 4.029-1.09 4.913-3.899l.52-1.663-9.852-27.293h6.082l6.863 22.146h.13l6.862-22.146h5.927l-10.216 28.67c-2.34 6.577-5.017 8.735-10.683 8.735-.442 0-1.872-.052-2.261-.157z"/></svg>`,
  google_pay: `<svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" aria-label="Google Pay"><path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z" fill="#000" opacity=".07"/><path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#FFF"/><path d="M18.093 11.976v3.2h-1.018v-7.9h2.691a2.447 2.447 0 0 1 1.747.692 2.28 2.28 0 0 1 .11 3.224l-.11.116c-.47.447-1.098.69-1.747.674l-1.673-.006zm0-3.732v2.788h1.698c.377.012.741-.135 1.005-.404a1.391 1.391 0 0 0-1.005-2.354l-1.698-.03zm6.484 1.348c.65-.03 1.286.188 1.778.613.445.43.682 1.03.65 1.649v3.334h-.969v-.766h-.049a1.93 1.93 0 0 1-1.673.931 2.17 2.17 0 0 1-1.496-.533 1.667 1.667 0 0 1-.613-1.324 1.606 1.606 0 0 1 .613-1.336 2.746 2.746 0 0 1 1.698-.515c.517-.02 1.03.093 1.49.331v-.208a1.134 1.134 0 0 0-.417-.901 1.416 1.416 0 0 0-.98-.368 1.545 1.545 0 0 0-1.319.717l-.895-.564a2.488 2.488 0 0 1 2.182-1.06zM23.29 13.52a.79.79 0 0 0 .337.662c.223.176.5.269.785.263.429-.001.84-.17 1.146-.472.305-.286.478-.685.478-1.103a2.047 2.047 0 0 0-1.324-.374 1.716 1.716 0 0 0-1.03.294.883.883 0 0 0-.392.73zm9.286-3.75l-3.39 7.79h-1.048l1.281-2.728-2.224-5.062h1.103l1.612 3.885 1.569-3.885h1.097z" fill="#5F6368"/><path d="M13.986 11.284c0-.308-.024-.616-.073-.92h-4.29v1.747h2.451a2.096 2.096 0 0 1-.9 1.373v1.134h1.464a4.433 4.433 0 0 0 1.348-3.334z" fill="#4285F4"/><path d="M9.629 15.721a4.352 4.352 0 0 0 3.01-1.097l-1.466-1.14a2.752 2.752 0 0 1-4.094-1.44H5.577v1.17a4.53 4.53 0 0 0 4.052 2.507z" fill="#34A853"/><path d="M7.079 12.05a2.709 2.709 0 0 1 0-1.735v-1.17H5.577a4.505 4.505 0 0 0 0 4.075l1.502-1.17z" fill="#FBBC04"/><path d="M9.629 8.44a2.452 2.452 0 0 1 1.74.68l1.3-1.293a4.37 4.37 0 0 0-3.065-1.183 4.53 4.53 0 0 0-4.027 2.5l1.502 1.171a2.715 2.715 0 0 1 2.55-1.875z" fill="#EA4335"/></svg>`,
  swish: `<svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Swish"><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z" fill="#000"/><path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32z" fill="#fff"/><path fill-rule="evenodd" clip-rule="evenodd" d="M25.239 15.231c.025-.241.037-.487.037-.737 0-.168-.009-.334-.024-.495a5.662 5.662 0 00-.049-.544c-.209-1.489-.868-2.79-1.979-3.901a7.384 7.384 0 00-1.389-1.1l1.545-1.92c.193.153.382.322.567.507a6.865 6.865 0 011.291 1.727c.509.975.762 2.05.762 3.226 0 .354-.02.701-.061 1.039a6.87 6.87 0 01-.7 2.198z" fill="url(#sp0)"/><path fill-rule="evenodd" clip-rule="evenodd" d="M21.836 8.455c.49.298.954.665 1.389 1.1 1.11 1.111 1.77 2.412 1.979 3.901.024.178.041.359.049.544.016.161.024.327.024.495 0 .25-.012.496-.037.737a7.633 7.633 0 01-1.291 1.728C22.58 18.319 20.932 19 19 19a6.93 6.93 0 01-2.728-.531 5.772 5.772 0 001.882.302c1.601 0 2.969-.567 4.103-1.703.379-.378.697-.785.954-1.22.032-.064.064-.125.097-.182.435-.813.651-1.715.651-2.705 0-1.603-.567-2.972-1.702-4.107a9.383 9.383 0 00-.421-.399z" fill="url(#sp1)"/><path fill-rule="evenodd" clip-rule="evenodd" d="M16.164 15.533a7.245 7.245 0 01-1.255-1.015c-1.159-1.159-1.827-2.521-2.004-4.083a2.773 2.773 0 01-.024-.362 5.01 5.01 0 01-.024-.496c0-.386.027-.757.084-1.111.308-.52.68-1 1.11-1.426C15.42 5.681 17.069 5 19 5c1.07 0 2.052.206 2.944.616a5.926 5.926 0 00-2.063-.351c-1.601 0-2.969.564-4.103 1.691-.367.37-.687.784-.954 1.232a8.204 8.204 0 00-.169.327c-.386.772-.579 1.626-.579 2.56 0 1.603.567 2.972 1.702 4.108.128.121.257.237.386.35z" fill="url(#sp2)"/><path fill-rule="evenodd" clip-rule="evenodd" d="M12.941 8.467a6.993 6.993 0 00-.084 1.111c0 .169.008.334.024.496 0 .121.008.241.024.362.177 1.562.845 2.923 2.004 4.083.394.396.813.733 1.255 1.015l-1.557 1.934c-.185-.162-.37-.331-.555-.508a7.52 7.52 0 01-1.159-1.51c-.596-1.024-.893-2.176-.893-3.456 0-.218.008-.431.024-.64a6.596 6.596 0 01.917-2.887z" fill="url(#sp3)"/><defs><radialGradient id="sp0" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(7.80944 0 0 7.81623 22.9 7.79)"><stop stop-color="#FDD626"/><stop offset=".365" stop-color="#F17345"/><stop offset=".663" stop-color="#F00912"/><stop offset=".878" stop-color="#EE0920"/><stop offset="1" stop-color="#E30767"/></radialGradient><radialGradient id="sp1" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(11.3723 0 0 11.3789 22.404 8.926)"><stop stop-color="#FDD626"/><stop offset=".314" stop-color="#EB5253"/><stop offset=".506" stop-color="#EB4F65"/><stop offset=".639" stop-color="#E739CB"/><stop offset=".8" stop-color="#9E16EC"/><stop offset="1" stop-color="#5702F0"/></radialGradient><radialGradient id="sp2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(11.214 0 0 11.2237 15.21 14.578)"><stop stop-color="#424EE9"/><stop offset=".392" stop-color="#58C9BE"/><stop offset=".765" stop-color="#6CD82E"/><stop offset="1" stop-color="#E5DB2A"/></radialGradient><radialGradient id="sp3" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(8.09806 0 0 8.10511 15.344 16.199)"><stop offset=".086" stop-color="#424EE9"/><stop offset=".447" stop-color="#58C9BE"/><stop offset=".71" stop-color="#59CAB5"/><stop offset="1" stop-color="#8CE785"/></radialGradient></defs></svg>`,
  mobilepay: `<svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" width="38" height="24" role="img" aria-label="MobilePay"><path fill="#000" opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill-rule="evenodd" clip-rule="evenodd" d="M20.05 15.296s2.53-.771 4.282-.776c2.896-.007 4.857 1.15 4.857 1.15V10.06s-1.97-1.02-4.453-1.09c-2.481-.068-4.687 1.012-4.687 1.012v5.313z" fill="#5A78FF"/><path fill-rule="evenodd" clip-rule="evenodd" d="M17.308 9.19l2.082 4.957V9.396s1.646-.882 3.485-1.13c1.84-.249 4.181.08 4.181.08l-1.133-2.707s-2.461-.159-4.69.794c-2.228.952-3.925 2.756-3.925 2.756z" fill="#5A78FF"/><path d="M21.428 5.676l-.82-1.99a1.818 1.818 0 00-2.37-.996l-5.663 2.334a1.818 1.818 0 00-.98 2.376l5.46 13.247a1.818 1.818 0 002.37.995l5.662-2.334a1.817 1.817 0 00.98-2.376l-.677-1.642s-.462-.027-.676-.033c-.226-.006-.644-.001-.644-.001l.88 2.136a.606.606 0 01-.326.793l-5.663 2.333a.606.606 0 01-.79-.331L12.711 6.94a.606.606 0 01.327-.792L18.7 3.814a.606.606 0 01.79.332l.83 2.011s.383-.187.59-.27c.206-.082.518-.211.518-.211z" fill="#5A78FF"/></svg>`,
  vipps: `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="24" aria-label="Vipps" role="img" viewBox="0 0 38 24"><path fill="#000" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z" opacity=".07"/><rect x="1" y="1" width="36" height="22" rx="2" fill="#FF5B24"/><g transform="translate(-1 0)"><path fill="#fff" d="m7.45 9.26 1.2 3.52 1.2-3.52h1.4l-2.08 5.22H8.13L6.05 9.26zm6.91 4.38a2.17 2.17 0 0 0 1.84-1.04c.26-.32.59-.4.83-.2a.6.6 0 0 1 0 .88 3.27 3.27 0 0 1-2.67 1.43 3.54 3.54 0 0 1-3-1.81.56.56 0 0 1 .06-.8.57.57 0 0 1 .8.24 2.58 2.58 0 0 0 2.14 1.3zm1.63-2.9a.7.7 0 1 1-.7-.7.71.71 0 0 1 .7.7zm3.31-1.48v.71a1.97 1.97 0 0 1 1.7-.84c1.01 0 2.2.84 2.2 2.67 0 1.9-1.13 2.82-2.31 2.82a1.88 1.88 0 0 1-1.6-.83v2.5h-1.28V9.26zm0 2.6c0 1.05.61 1.62 1.3 1.62.67 0 1.31-.51 1.31-1.62 0-1.09-.64-1.6-1.31-1.6-.69 0-1.3.49-1.3 1.6zm6.13-2.6v.71a1.97 1.97 0 0 1 1.69-.84c1.02 0 2.2.84 2.2 2.67 0 1.9-1.13 2.82-2.3 2.82a1.88 1.88 0 0 1-1.6-.83v2.5h-1.28V9.26zm0 2.6c0 1.05.61 1.62 1.3 1.62.67 0 1.31-.51 1.31-1.62 0-1.09-.64-1.6-1.31-1.6-.69 0-1.3.49-1.3 1.6zm6.6-2.74a2.04 2.04 0 0 1 2.13 1.72l-1.15.18c-.02-.6-.4-.82-.96-.82-.43 0-.76.19-.76.5 0 .24.16.48.66.56l.88.17a1.47 1.47 0 0 1 1.3 1.48 1.8 1.8 0 0 1-1.97 1.7 2.15 2.15 0 0 1-2.31-1.8l1.15-.18c.08.65.48.89 1.14.89.49 0 .82-.2.82-.5 0-.28-.16-.5-.7-.6l-.8-.14a1.54 1.54 0 0 1-1.37-1.5 1.8 1.8 0 0 1 1.93-1.68z"/></g></svg>`,
  vipps_mobilepay: `<svg viewBox="0 0 50 24" xmlns="http://www.w3.org/2000/svg" role="img" width="50" height="24" aria-label="Vipps MobilePay"><path opacity=".07" d="M47 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h44c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path d="M3 1h22v22H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2z" fill="#FF5B24"/><path d="M25 1h22c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H25V1z" fill="#5B7BFF"/><circle cx="16" cy="9" r="1.6" fill="#fff"/><path d="M9 13.5 Q14 18.5 19 13.5" stroke="#fff" stroke-width="1.7" stroke-linecap="round" fill="none"/><rect x="32" y="6" width="7" height="12" rx="1.4" fill="none" stroke="#fff" stroke-width="1.4"/><path d="M39 9.5 q3.5 1.5 1.5 5" stroke="#fff" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>`,
};

function paymentMethodLabel(id: string): string {
  switch (id) {
    case "credit-card": return "Credit / Debit Card";
    case "apple_pay": return "Apple Pay";
    case "google_pay": return "Google Pay";
    case "swish": return "Swish";
    case "vipps": return "Vipps";
    case "mobilepay": return "MobilePay";
    case "vipps_mobilepay": return "Vipps MobilePay";
    default: return id.replace(/[-_]/g, " ");
  }
}

function paymentMethodHint(id: string): string | null {
  switch (id) {
    case "apple_pay":
    case "google_pay":
      return "You'll be redirected to complete your purchase.";
    case "swish":
      return "Open the Swish app on your phone to pay.";
    case "vipps":
    case "mobilepay":
    case "vipps_mobilepay":
      return "You'll be redirected to your mobile wallet.";
    default:
      return null;
  }
}

function normalizePaymentMethodId(method: string): string {
  switch (method) {
    case "apple-pay":
      return "apple_pay";
    case "google-pay":
      return "google_pay";
    case "shop-pay":
      return "shop_pay";
    default:
      return method;
  }
}

function walletMethodAllowedInPreview(
  method: "apple_pay" | "google_pay" | "shop_pay",
  mode: WalletPreviewMode,
): boolean {
  if (mode === "design") return true;
  if (method === "shop_pay") return true;
  if (mode === "unavailable") return false;
  if (method === "apple_pay") return mode === "ios_safari" || mode === "mac_safari";
  if (method === "google_pay") return mode === "chrome_desktop" || mode === "android_chrome";
  return false;
}

function resolvePreviewPaymentMethods(methods: string[], mode: WalletPreviewMode): string[] {
  if (mode === "design") return methods;
  return methods.filter((method) => {
    const normalized = normalizePaymentMethodId(method);
    if (normalized === "apple_pay" || normalized === "google_pay" || normalized === "shop_pay") {
      return walletMethodAllowedInPreview(normalized, mode);
    }
    return true;
  });
}

// ─── Block components ────────────────────────────────────────────────────────

function BlockEmail() {
  return (
    <div className="field">
      <label>Email</label>
      <input type="email" placeholder="you@example.com" readOnly />
    </div>
  );
}

function BlockMarketingConsent({ cfg }: { cfg?: LayoutBlockConfig["marketing_consent"] }) {
  return (
    <label className="consent">
      <input type="checkbox" defaultChecked={cfg?.default_opt_in ?? false} readOnly />
      <span>{cfg?.copy ?? "Email me with offers and updates"}</span>
    </label>
  );
}

function BlockPhone({ cfg }: { cfg?: LayoutBlockConfig["phone"] }) {
  return (
    <div className="field">
      <label>Phone {cfg?.required ? "" : "(optional)"}</label>
      <input type="tel" placeholder="+33 1 23 45 67 89" readOnly />
    </div>
  );
}

function BlockCompany({ cfg }: { cfg?: LayoutBlockConfig["company_field"] }) {
  return (
    <div className="field">
      <label>Company {cfg?.required ? "" : "(optional)"}</label>
      <input type="text" placeholder="Company name" readOnly />
    </div>
  );
}

function BlockAddress({ cfg }: { cfg?: LayoutBlockConfig["address"] }) {
  const showCompany = !!cfg?.show_company;
  const showVat = !!cfg?.show_vat_id;
  const showPO = !!cfg?.show_po_number;
  const showBilling = !!cfg?.show_billing_same_as_shipping;
  const billingDefaultSame = cfg?.billing_default_same !== false;

  return (
    <>
      <AddressGrid
        namePrefix="shipping"
        showCompany={showCompany}
        companyRequired={!!cfg?.company_required}
        showVat={showVat}
        showPO={showPO}
        poRequired={!!cfg?.po_number_required}
      />
      {showBilling ? (
        <div className="billing-choice">
          <label className="consent">
            <input
              type="checkbox"
              name="billing_same_as_shipping"
              defaultChecked={billingDefaultSame}
            />
            <span>Billing address same as shipping</span>
          </label>
          <div className="billing-grid-wrap">
            <p className="section-subtitle">Billing address</p>
            <AddressGrid
              namePrefix="billing"
              showCompany={showCompany}
              companyRequired={!!cfg?.company_required}
              showVat={false}
              showPO={false}
              poRequired={false}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}

function AddressGrid({
  namePrefix,
  showCompany,
  companyRequired,
  showVat,
  showPO,
  poRequired,
}: {
  namePrefix: "shipping" | "billing";
  showCompany: boolean;
  companyRequired: boolean;
  showVat: boolean;
  showPO: boolean;
  poRequired: boolean;
}) {
  const n = (k: string) => `${namePrefix}_${k}`;
  return (
    <div className="address-grid">
      <div className="field"><label>First name</label><input type="text" name={n("first_name")} readOnly /></div>
      <div className="field"><label>Last name</label><input type="text" name={n("last_name")} readOnly /></div>
      {showCompany ? (
        <div className="field wide">
          <label>Company{companyRequired ? "" : " (optional)"}</label>
          <input type="text" name={n("company")} readOnly />
        </div>
      ) : null}
      <div className="field wide"><label>Address</label><input type="text" name={n("address1")} readOnly /></div>
      <div className="field wide"><label>Apt, suite (optional)</label><input type="text" name={n("address2")} readOnly /></div>
      <div className="field"><label>City</label><input type="text" name={n("city")} readOnly /></div>
      <div className="field"><label>Postal code</label><input type="text" name={n("zip")} readOnly /></div>
      <div className="field wide"><label>Country</label><input type="text" name={n("country_code")} readOnly /></div>
      {showVat ? (
        <div className="field wide">
          <label>VAT ID (optional)</label>
          <input type="text" name={n("vat_id")} placeholder="e.g. GB123456789" readOnly />
        </div>
      ) : null}
      {showPO ? (
        <div className="field wide">
          <label>Purchase order #{poRequired ? "" : " (optional)"}</label>
          <input type="text" name={n("po_number")} readOnly />
        </div>
      ) : null}
    </div>
  );
}

function BlockShippingMethod() {
  const rates = [
    { id: "std", title: "Standard (3-5 days)", price: "€5.00", checked: true },
    { id: "exp", title: "Express (next day)", price: "€15.00", checked: false },
  ];
  return (
    <div className="shipping-rates">
      {rates.map((r) => (
        <label key={r.id} className="rate">
          <input type="radio" name="shipping_rate_id" defaultChecked={r.checked} />
          <span className="rate-title">{r.title}</span>
          <span className="rate-price">{r.price}</span>
        </label>
      ))}
    </div>
  );
}

function BlockOrderNotes() {
  return (
    <div className="field">
      <label>Order notes</label>
      <textarea rows={2} readOnly />
    </div>
  );
}

function BlockGiftMessage({ cfg }: { cfg?: LayoutBlockConfig["gift_message"] }) {
  return (
    <div className="field">
      <label>Gift message</label>
      <textarea rows={2} maxLength={cfg?.max_length ?? 250} readOnly />
    </div>
  );
}

function BlockShippingInsurance({ cfg }: { cfg?: LayoutBlockConfig["shipping_insurance"] }) {
  if (!cfg) return null;
  return (
    <label className="consent insurance">
      <input type="checkbox" readOnly />
      <span>Protect my order with shipping insurance</span>
      <span className="rate-price">+{formatCents(cfg.fee_cents, cfg.currency)}</span>
    </label>
  );
}

function BlockBillingSameAs({ cfg }: { cfg?: LayoutBlockConfig["billing_same_as_shipping"] }) {
  const sameDefault = cfg?.default_checked !== false;
  return (
    <div className="billing-choice">
      <p className="section-subtitle">Billing address</p>
      <div className="method-stack">
        <label className="method method-simple">
          <div className="method-header">
            <input type="radio" name="billing_same_as_shipping" defaultChecked={sameDefault} />
            <span className="method-label">Same as shipping address</span>
          </div>
        </label>
        <label className="method method-simple">
          <div className="method-header">
            <input type="radio" name="billing_same_as_shipping" defaultChecked={!sameDefault} />
            <span className="method-label">Use a different billing address</span>
          </div>
        </label>
      </div>
    </div>
  );
}

function BlockPaymentMethods({
  node,
  cfg,
  walletPreviewMode,
}: {
  node: CheckoutPageNode;
  cfg?: LayoutBlockConfig["payment_method_selector"];
  walletPreviewMode: WalletPreviewMode;
}) {
  const paymentMethods = resolvePreviewPaymentMethods(node.payment_methods, walletPreviewMode);
  if (paymentMethods.length === 0) {
    // Builder shows a warning banner; preview stays silent so the layout
    // looks the same as what buyers would see (pre-publish validation
    // stops this state from ever reaching production).
    return null;
  }
  const showIcons = cfg?.show_brand_icons !== false;
  const maxVisible = cfg?.max_visible_icons ?? 3;
  return (
    <div className="payment-methods">
      <p className="section-subtitle">All transactions are secure and encrypted.</p>
      <div className="method-stack">
        {paymentMethods.map((id, i) => (
          <PaymentMethodCard
            key={id}
            methodId={id}
            checked={i === 0}
            showIcons={showIcons}
            maxVisible={maxVisible}
            iconOverrides={cfg?.brand_icon_overrides}
          />
        ))}
      </div>
    </div>
  );
}

function PaymentMethodCard({
  methodId,
  checked,
  showIcons,
  maxVisible,
  iconOverrides,
}: {
  methodId: string;
  checked: boolean;
  showIcons: boolean;
  maxVisible: number;
  iconOverrides?: Record<string, string[]>;
}) {
  const label = paymentMethodLabel(methodId);
  const hint = paymentMethodHint(methodId);
  const fallbackIcons = iconsForMethod(methodId);
  const icons = iconOverrides?.[methodId] ?? fallbackIcons;
  const visible = showIcons ? icons.slice(0, maxVisible) : [];
  const overflow = showIcons ? icons.length - visible.length : 0;
  return (
    <label className="method">
      <div className="method-header">
        <input type="radio" name="payment_method" defaultChecked={checked} />
        <span className="method-label">{label}</span>
        {showIcons && icons.length > 0 ? (
          <span className="method-icons">
            {visible.map((b) => (
              <span
                key={b}
                className="method-icon"
                dangerouslySetInnerHTML={{ __html: BRAND_SVG[b] ?? "" }}
              />
            ))}
            {overflow > 0 ? (
              <span
                className="method-icon method-icon-overflow"
                title={icons.slice(maxVisible).join(", ")}
              >
                +{overflow}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      {hint ? (
        <div className="method-body">
          <p className="method-hint">{hint}</p>
        </div>
      ) : null}
    </label>
  );
}

function BlockPayButton({ ctx, node }: { ctx: BuyerCtx; node: CheckoutPageNode }) {
  const total = ctx.cart.subtotal_cents + ctx.cart.tax_cents + ctx.cart.shipping_cents;
  // copy_overrides["pay_button"] is `string | Record<string, string>` —
  // flat-shape wins here (preview is locale-agnostic); a per-locale
  // value falls back to the default label.
  const raw = node.copy_overrides?.["pay_button"];
  const label = typeof raw === "string" ? raw : "Pay";
  return (
    <button type="button" className="pay-btn">
      {label} {formatCents(total, ctx.cart.currency)}
    </button>
  );
}

function BlockLineItems({ ctx }: { ctx: BuyerCtx }) {
  // Mirror the buyer-page Shopify-style layout: 48px rounded thumbnail
  // on the left with a small qty badge in the top-right, name in the
  // middle, price on the right. Studio's demo cart doesn't ship real
  // image URLs so we always render the muted placeholder square here;
  // the live buyer page swaps in the real image when present.
  return (
    <ul className="line-items">
      {ctx.cart.items.map((item) => (
        <li key={item.sku} className="li-with-thumb">
          <div className="li-thumb-wrap">
            <div className="li-thumb-placeholder" />
            <span className="li-qty-badge">{item.qty}</span>
          </div>
          <span className="li-name">{item.name}</span>
          <span className="li-price">
            {formatCents(item.price_cents * item.qty, ctx.cart.currency)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BlockTotals({ ctx }: { ctx: BuyerCtx }) {
  const total = ctx.cart.subtotal_cents + ctx.cart.tax_cents + ctx.cart.shipping_cents;
  return (
    <dl className="totals">
      <Row label="Subtotal" value={formatCents(ctx.cart.subtotal_cents, ctx.cart.currency)} />
      <Row label="Shipping" value={formatCents(ctx.cart.shipping_cents, ctx.cart.currency)} />
      <Row label="Tax" value={formatCents(ctx.cart.tax_cents, ctx.cart.currency)} />
      <Row label="Total" value={formatCents(total, ctx.cart.currency)} bold />
    </dl>
  );
}

function BlockPromoCode({ cfg }: { cfg?: LayoutBlockConfig["promo_code"] }) {
  return (
    <div className="promo">
      <input placeholder="Promo code" readOnly />
      <button type="button" className="promo-apply">
        {cfg?.auto_apply_on_entry ? "Applied" : "Apply"}
      </button>
    </div>
  );
}

function BlockTrustBadges({ cfg }: { cfg?: LayoutBlockConfig["trust_badges"] }) {
  const mode = cfg?.mode ?? "payment_icons";
  const alignment = cfg?.alignment ?? "center";
  if (mode === "payment_icons") {
    const icons = cfg?.icon_ids ?? ["visa", "mastercard", "amex", "mobilepay", "vipps"];
    return (
      <div
        className="trust-badges trust-icons"
        data-align={alignment}
      >
        {icons.map((b) => (
          <span
            key={b}
            className="method-icon trust-icon"
            dangerouslySetInnerHTML={{ __html: BRAND_SVG[b] ?? "" }}
          />
        ))}
      </div>
    );
  }
  const badges = cfg?.badge_ids ?? ["ssl", "pci"];
  return (
    <div className="trust-badges" data-align={alignment}>
      {badges.map((b) => (
        <span key={b} className="badge">{b}</span>
      ))}
    </div>
  );
}

function BlockDivider() {
  return <hr className="divider" />;
}

function BlockTextBanner({ cfg }: { cfg?: LayoutBlockConfig["text_banner"] }) {
  if (!cfg?.copy) return <div className="text-banner muted">Text banner — empty</div>;
  return <div className="text-banner">{cfg.copy}</div>;
}

function BlockImageBanner({ cfg }: { cfg?: LayoutBlockConfig["image_banner"] }) {
  if (!cfg?.image_id) {
    return (
      <div className="text-banner muted" style={{ textAlign: "center" }}>
        No image selected
      </div>
    );
  }
  return (
    <img
      src={cfg.image_id.startsWith("data:") ? cfg.image_id : `https://cdn.costplus.io/banners/${cfg.image_id}`}
      alt={cfg.alt ?? "Banner"}
      className="image-banner"
    />
  );
}

function BlockCustomHtml({
  cfg,
}: {
  cfg?: { html_id: string; sandbox_height_px: number };
}) {
  const sanitized = useCustomHtml(cfg?.html_id ?? "");
  if (!cfg?.html_id) {
    return <div className="text-banner muted">Custom HTML block — click to edit</div>;
  }
  return (
    <iframe
      srcDoc={sanitized}
      sandbox="allow-same-origin"
      style={{ width: "100%", border: 0, height: cfg.sandbox_height_px ?? 60 }}
      title="Custom HTML block"
    />
  );
}

function useCustomHtml(id: string): string {
  return useMemo(() => (id ? loadCustomHtml(id) : ""), [id]);
}

// ─── Dispatch ────────────────────────────────────────────────────────────────

export function renderBlock(
  id: BlockId,
  ctx: BuyerCtx,
  node: CheckoutPageNode,
  walletPreviewMode: WalletPreviewMode = "design",
): ReactNode {
  // Custom HTML — instance ids like `custom_html_abc123`. Multiple per
  // section; each holds its own config entry. Detected by prefix so new
  // instances Just Work without case enumeration.
  if (id.startsWith("custom_html_")) {
    const key = id as `custom_html_${string}`;
    return <BlockCustomHtml cfg={ctx.blockConfig[key]} />;
  }
  switch (id) {
    case "email": return <BlockEmail />;
    case "marketing_consent": return <BlockMarketingConsent cfg={ctx.blockConfig.marketing_consent} />;
    case "phone": return <BlockPhone cfg={ctx.blockConfig.phone} />;
    case "company_field": return <BlockCompany cfg={ctx.blockConfig.company_field} />;
    case "address": return <BlockAddress cfg={ctx.blockConfig.address} />;
    case "shipping_method": return <BlockShippingMethod />;
    case "order_notes": return <BlockOrderNotes />;
    case "gift_message": return <BlockGiftMessage cfg={ctx.blockConfig.gift_message} />;
    case "shipping_insurance": return <BlockShippingInsurance cfg={ctx.blockConfig.shipping_insurance} />;
    case "payment_method_selector": return <BlockPaymentMethods node={node} cfg={ctx.blockConfig.payment_method_selector} walletPreviewMode={walletPreviewMode} />;
    case "billing_same_as_shipping": return <BlockBillingSameAs cfg={ctx.blockConfig.billing_same_as_shipping} />;
    case "pay_button": return <BlockPayButton ctx={ctx} node={node} />;
    case "line_items": return <BlockLineItems ctx={ctx} />;
    case "totals": return <BlockTotals ctx={ctx} />;
    case "promo_code": return <BlockPromoCode cfg={ctx.blockConfig.promo_code} />;
    case "trust_badges": return <BlockTrustBadges cfg={ctx.blockConfig.trust_badges} />;
    case "divider": return <BlockDivider />;
    case "text_banner": return <BlockTextBanner cfg={ctx.blockConfig.text_banner} />;
    case "image_banner": return <BlockImageBanner cfg={ctx.blockConfig.image_banner} />;
    // ─── Wave 3 additions (Studio preview) ─────────────────────────────────
    case "newsletter_opt_in": return <BlockConsent
      name="accepts_newsletter"
      defaultChecked={ctx.blockConfig.newsletter_opt_in?.default_opt_in}
      copy={ctx.blockConfig.newsletter_opt_in?.copy ?? "Sign me up for the newsletter — I can unsubscribe any time."}
    />;
    case "sms_opt_in": return <BlockConsent
      name="accepts_sms"
      defaultChecked={ctx.blockConfig.sms_opt_in?.default_opt_in}
      copy={ctx.blockConfig.sms_opt_in?.copy ?? "Text me order updates and offers. Msg & data rates may apply. Reply STOP to opt out."}
    />;
    case "login_link": return <BlockLoginLink cfg={ctx.blockConfig.login_link} />;
    case "loyalty_login_card": return <BlockLoyaltyLoginCard cfg={ctx.blockConfig.loyalty_login_card} />;
    case "save_my_info": return <BlockConsent
      name="save_my_info"
      defaultChecked={ctx.blockConfig.save_my_info?.default_checked !== false}
      copy={ctx.blockConfig.save_my_info?.copy ?? "Save my info for a faster checkout next time"}
    />;
    case "vat_id_field": return <BlockPlainField label="VAT ID" name="vat_id" placeholder="e.g. GB123456789" />;
    case "po_number": return <BlockPlainField label="Purchase order #" name="po_number" />;
    case "delivery_date": return <BlockPlainField label="Preferred delivery date" name="delivery_date" type="date" />;
    case "pickup_point": return <BlockPickupPoint />;
    case "save_card_toggle": return <BlockConsent
      name="save_card"
      defaultChecked={ctx.blockConfig.save_card_toggle?.default_checked !== false}
      copy={ctx.blockConfig.save_card_toggle?.copy ?? "Save this card for faster checkout next time"}
    />;
    case "terms_checkbox": return <BlockTerms cfg={ctx.blockConfig.terms_checkbox} />;
    case "age_verification": return <BlockConsent
      name="age_verified"
      defaultChecked={false}
      copy={`I confirm I am at least ${ctx.blockConfig.age_verification?.minimum_age ?? 18} years old`}
    />;
    case "subscription_consent": return <BlockSubscriptionConsent cfg={ctx.blockConfig.subscription_consent} />;
    case "tax_breakdown": return <BlockTaxBreakdown />;
    case "gift_card_input": return <BlockGiftCardInput cfg={ctx.blockConfig.gift_card_input} />;
    case "ic_fee_transparency": return <BlockIcFeeTransparency />;
    case "product_thumbnails": return <BlockProductThumbnails ctx={ctx} cfg={ctx.blockConfig.product_thumbnails} />;
    // ─── Wave 4 blocks (Studio preview) ────────────────────────────────────
    case "upsell_slot": return <BlockUpsellSlot cfg={ctx.blockConfig.upsell_slot} />;
    case "recommendation_cards": return <BlockRecommendationCards ctx={ctx} cfg={ctx.blockConfig.recommendation_cards} />;
    case "incentive_banner": return <BlockIncentiveBanner cfg={ctx.blockConfig.incentive_banner} ctx={ctx} />;
    case "benefit_rows": return <BlockBenefitRows cfg={ctx.blockConfig.benefit_rows} />;
    case "testimonial_cards": return <BlockTestimonialCards cfg={ctx.blockConfig.testimonial_cards} />;
    case "legal_links": return <BlockLegalLinks cfg={ctx.blockConfig.legal_links} />;
    case "cookie_notice": return <BlockCookieNotice cfg={ctx.blockConfig.cookie_notice} />;
    case "support_block": return <BlockSupportBlock cfg={ctx.blockConfig.support_block} />;
    case "help_center_link": return <BlockHelpCenterLink cfg={ctx.blockConfig.help_center_link} />;
  }
  // Template-literal BlockId members (like `custom_html_${string}`) that
  // weren't matched above fall through here; they were already handled by
  // the prefix dispatch at the top of this function.
  return null;
}

// ─── Wave 4 preview components ──────────────────────────────────────────────

function BlockUpsellSlot({ cfg }: { cfg?: { headline: string; max_items: number } }) {
  const headline = cfg?.headline ?? "You might also like";
  const maxItems = Math.max(1, Math.min(cfg?.max_items ?? 3, 4));
  const demoItems = [
    { name: "Gift wrap", price: "$4.99" },
    { name: "Express shipping", price: "$9.99" },
    { name: "Extended warranty", price: "$12.00" },
    { name: "Engraving", price: "$8.00" },
  ].slice(0, maxItems);
  return (
    <div>
      <p className="section-subtitle" style={{ marginBottom: 8 }}>{headline}</p>
      <div style={{ display: "grid", gap: 8 }}>
        {demoItems.map((item) => (
          <div
            key={item.name}
            className="method"
            style={{ display: "flex", alignItems: "center", padding: 10, gap: 10 }}
          >
            <span
              style={{
                width: 36,
                height: 36,
                background: "var(--cp-surface-muted)",
                borderRadius: "var(--cp-radius)",
                flex: "none",
              }}
            />
            <span style={{ flex: 1, fontSize: "0.9rem" }}>{item.name}</span>
            <span style={{ fontWeight: 600 }}>{item.price}</span>
            <button type="button" className="promo-apply" disabled>
              Add
            </button>
          </div>
        ))}
      </div>
      <p className="muted" style={{ fontSize: "0.75rem", marginTop: 6 }}>
        Live recommendations hook up when the merchant wires a product-feed provider.
      </p>
    </div>
  );
}

function BlockRecommendationCards({
  ctx,
  cfg,
}: {
  ctx: BuyerCtx;
  cfg?: LayoutBlockConfig["recommendation_cards"];
}) {
  const items: Array<{
    title: string;
    subtitle?: string;
    price_cents?: number;
    image_url?: string;
    button_label?: string;
  }> = cfg?.items?.length
    ? cfg.items
    : ctx.cart.items.slice(0, 2).map((item) => ({
        title: item.name,
        subtitle: "Popular with this order",
        price_cents: item.price_cents,
        button_label: "Add",
      }));
  return (
    <div className="recommendation-cards">
      <p className="section-subtitle">{cfg?.title ?? "Most customers also like"}</p>
      {items.map((item, idx) => (
        <div className="recommendation-card" key={`${item.title}-${idx}`}>
          {item.image_url ? (
            <img src={item.image_url} alt="" className="recommendation-thumb" />
          ) : (
            <span className="recommendation-thumb placeholder" aria-hidden="true" />
          )}
          <span className="recommendation-copy">
            <span className="recommendation-title">{item.title}</span>
            {item.subtitle ? <span className="recommendation-subtitle">{item.subtitle}</span> : null}
            {typeof item.price_cents === "number" ? (
              <span className="recommendation-price">
                {formatCents(item.price_cents, ctx.cart.currency)}
              </span>
            ) : null}
          </span>
          <button type="button" className="recommendation-add" disabled>
            {item.button_label ?? "Add"}
          </button>
        </div>
      ))}
    </div>
  );
}

function BlockIncentiveBanner({
  cfg,
  ctx,
}: {
  cfg?: { copy: string; target_amount_cents?: number };
  ctx: BuyerCtx;
}) {
  const target = cfg?.target_amount_cents ?? 15000;
  const subtotal = ctx.cart.subtotal_cents;
  const remaining = Math.max(0, target - subtotal);
  const defaultCopy =
    remaining > 0
      ? `Add ${formatCents(remaining, ctx.cart.currency)} more for free shipping`
      : "You've unlocked free shipping!";
  const copy = cfg?.copy && cfg.copy !== "" ? cfg.copy : defaultCopy;
  return (
    <div
      className="text-banner"
      style={{
        background: "color-mix(in srgb, var(--cp-brand) 8%, transparent)",
        borderLeft: "3px solid var(--cp-brand)",
        paddingLeft: 12,
      }}
    >
      {copy}
    </div>
  );
}

function BlockBenefitRows({ cfg }: { cfg?: LayoutBlockConfig["benefit_rows"] }) {
  const rows = cfg?.rows?.length
    ? cfg.rows
    : [
        { title: "Trusted by happy customers", copy: "Clear pricing before you pay" },
        { title: "Secure checkout", copy: "Encrypted payment handoff" },
        { title: "Fast dispatch", copy: "Delivery options update from your address" },
      ];
  return (
    <div className="benefit-rows">
      {rows.map((row) => (
        <div className="benefit-row" key={row.title}>
          <span className="benefit-icon" aria-hidden="true" />
          <span className="benefit-copy">
            <strong>{row.title}</strong>
            {row.copy ? <small>{row.copy}</small> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function BlockTestimonialCards({ cfg }: { cfg?: LayoutBlockConfig["testimonial_cards"] }) {
  const reviews = cfg?.reviews?.length
    ? cfg.reviews
    : [
        { quote: "Fast checkout and clear delivery price.", author: "Verified buyer", rating: 5 },
        { quote: "Everything was easy to review before payment.", author: "Returning customer", rating: 5 },
      ];
  return (
    <div className="testimonial-cards">
      {cfg?.title ? <p className="section-subtitle">{cfg.title}</p> : null}
      {reviews.map((review) => (
        <figure className="testimonial-card" key={`${review.author}-${review.quote}`}>
          <blockquote>{review.quote}</blockquote>
          <figcaption>
            <span>{review.author ?? "Customer"}</span>
            <span className="testimonial-rating">{review.rating ?? 5}/5</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function BlockLegalLinks({
  cfg,
}: {
  cfg?: { terms_url?: string; privacy_url?: string; refund_url?: string; cookies_url?: string };
}) {
  const links: { label: string; href: string | undefined }[] = [
    { label: "Terms", href: cfg?.terms_url },
    { label: "Privacy", href: cfg?.privacy_url },
    { label: "Refund policy", href: cfg?.refund_url },
    { label: "Cookies", href: cfg?.cookies_url },
  ].filter((l) => l.href);
  if (links.length === 0) {
    return <p className="muted" style={{ fontSize: "0.8rem" }}>No legal links configured.</p>;
  }
  return (
    <p className="muted" style={{ fontSize: "0.8rem" }}>
      {links.map((l, i) => (
        <span key={l.label}>
          <a href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
          {i < links.length - 1 ? " · " : ""}
        </span>
      ))}
    </p>
  );
}

function BlockCookieNotice({ cfg }: { cfg?: { copy: string } }) {
  const copy = cfg?.copy ?? "We use cookies to improve your experience. By continuing you accept our cookie policy.";
  return <p className="muted" style={{ fontSize: "0.8rem" }}>{copy}</p>;
}

function BlockSupportBlock({
  cfg,
}: {
  cfg?: { email: string; chat_provider?: "none" | "intercom" | "crisp" | "custom" };
}) {
  const email = cfg?.email ?? "support@example.com";
  const provider = cfg?.chat_provider ?? "none";
  return (
    <div>
      <p style={{ margin: "0 0 6px", fontWeight: 500 }}>Need help?</p>
      <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
        Email: <a href={`mailto:${email}`}>{email}</a>
      </p>
      {provider !== "none" ? (
        <p className="muted" style={{ margin: "4px 0 0", fontSize: "0.75rem" }}>
          Chat widget: {provider} (placeholder — embed pending)
        </p>
      ) : null}
    </div>
  );
}

function BlockHelpCenterLink({
  cfg,
}: {
  cfg?: { label: string; url: string };
}) {
  const label = cfg?.label ?? "Visit the help center";
  const url = cfg?.url ?? "#";
  return (
    <p className="muted" style={{ fontSize: "0.85rem" }}>
      <a href={url} target="_blank" rel="noopener noreferrer">{label}</a>
    </p>
  );
}

// ─── Wave 3 preview components ───────────────────────────────────────────────

function BlockConsent({
  name,
  defaultChecked,
  copy,
}: {
  name: string;
  defaultChecked?: boolean;
  copy: string;
}) {
  return (
    <label className="consent">
      <input type="checkbox" name={name} defaultChecked={!!defaultChecked} />
      <span>{copy}</span>
    </label>
  );
}

function BlockLoginLink({
  cfg,
}: {
  cfg?: { copy: string; href: string };
}) {
  const copy = cfg?.copy ?? "Already have an account? Sign in";
  const href = cfg?.href ?? "#";
  return (
    <p className="muted">
      <a href={href}>{copy}</a>
    </p>
  );
}

function BlockLoyaltyLoginCard({
  cfg,
}: {
  cfg?: { title?: string; copy?: string; link_label?: string; href?: string };
}) {
  return (
    <div className="loyalty-card">
      <span className="loyalty-card-icon" aria-hidden="true">?</span>
      <div className="loyalty-card-body">
        <p className="loyalty-card-title">{cfg?.title ?? "Have rewards or discounts?"}</p>
        <a href={cfg?.href ?? "#"}>{cfg?.link_label ?? cfg?.copy ?? "Sign in to apply them"}</a>
      </div>
    </div>
  );
}

function BlockPlainField({
  label,
  name,
  placeholder,
  type = "text",
}: {
  label: string;
  name: string;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={`f_${name}`}>{label}</label>
      <input id={`f_${name}`} name={name} type={type} placeholder={placeholder} />
    </div>
  );
}

function BlockPickupPoint() {
  return (
    <div className="field">
      <label>Pickup point</label>
      <button type="button" className="promo-apply" disabled>
        Choose a pickup point (coming soon)
      </button>
      <p className="muted" style={{ fontSize: "0.75rem", marginTop: 4 }}>
        Carrier pickup points launch when we finalize the PostNord / Bring integration.
      </p>
    </div>
  );
}

function BlockTerms({
  cfg,
}: {
  cfg?: { required: boolean; copy: string; terms_url: string; link_title?: string };
}) {
  const copy = cfg?.copy ?? "I agree to the";
  const href = cfg?.terms_url ?? "#";
  const linkTitle = cfg?.link_title ?? "Terms & Conditions";
  return (
    <label className="consent">
      <input type="checkbox" name="accepts_terms" defaultChecked={false} />
      <span>
        {copy}{" "}
        <a href={href} className="terms-link" target="_blank" rel="noopener noreferrer">
          {linkTitle}
        </a>
      </span>
    </label>
  );
}

function BlockSubscriptionConsent({
  cfg,
}: {
  cfg?: { copy: string };
}) {
  const copy =
    cfg?.copy ??
    "Your cart includes a subscription. By paying, you authorize recurring charges at the stated interval until you cancel.";
  return (
    <div className="text-banner">
      <strong>Recurring charge authorization</strong>
      <p style={{ margin: "4px 0 0", fontSize: "0.85rem" }}>{copy}</p>
    </div>
  );
}

function BlockTaxBreakdown() {
  return (
    <details className="tax-breakdown">
      <summary>Tax breakdown (3 lines)</summary>
      <dl style={{ margin: 0 }}>
        <div className="row">
          <dt>Sales tax · 8.5%</dt>
          <dd>$9.95</dd>
        </div>
        <div className="row">
          <dt>State · 2.0%</dt>
          <dd>$2.34</dd>
        </div>
        <div className="row">
          <dt>Local · 1.0%</dt>
          <dd>$1.17</dd>
        </div>
      </dl>
    </details>
  );
}

function BlockGiftCardInput({
  cfg,
}: {
  cfg?: { label: string };
}) {
  const label = cfg?.label ?? "Gift card or store credit";
  return (
    <div className="promo">
      <input type="text" name="gift_card_code" placeholder={label} />
      <button type="button" className="promo-apply" disabled>
        Apply
      </button>
    </div>
  );
}

function BlockIcFeeTransparency() {
  return (
    <details className="ic-transparency">
      <summary>Fee transparency (IC++)</summary>
      <p className="muted" style={{ fontSize: "0.8rem" }}>
        We bill you the network interchange + scheme + our fee — no markup. Line-item
        breakdown arrives when the fee data lands on the session payload.
      </p>
    </details>
  );
}

function BlockProductThumbnails({
  ctx,
  cfg,
}: {
  ctx: BuyerCtx;
  cfg?: { size_px: number };
}) {
  const size = cfg?.size_px ?? 48;
  return (
    <ul className="line-items line-items-thumb" style={{ padding: 0 }}>
      {ctx.cart.items.map((it) => (
        <li
          key={it.sku}
          className="li-thumb-row"
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}
        >
          <span
            className="li-thumb"
            style={{
              width: size,
              height: size,
              background: "var(--cp-surface-muted)",
              borderRadius: "var(--cp-radius)",
              display: "inline-block",
              flex: "none",
            }}
          />
          <span style={{ flex: 1 }}>
            {it.name} × {it.qty}
          </span>
          <span>{formatCents(it.price_cents * it.qty, ctx.cart.currency)}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Page composition ─────────────────────────────────────────────────────────

export function CheckoutPagePreview({
  node,
  ctx = DEMO_CTX,
  viewport = "desktop",
  walletPreviewMode = "design",
  selection,
  onSelectBlock,
  shopifyShopDomain,
}: {
  node: CheckoutPageNode;
  ctx?: BuyerCtx;
  viewport?: Device;
  walletPreviewMode?: WalletPreviewMode;
  /** Currently selected block (rendered with an outline). */
  selection?: { block: BlockId } | null;
  /** If set, each block becomes clickable and selects itself. */
  onSelectBlock?: (block: BlockId) => void;
  /** When set + matches *.myshopify.com, renders a small cart icon
   *  top-right of the topbar. Mirrors the buyer page's behaviour. */
  shopifyShopDomain?: string | null;
}) {
  const layout = node.layout_blocks ?? defaultLayout();
  const ctxMerged: BuyerCtx = { ...ctx, blockConfig: layout.block_config ?? {} };
  const sections = layout.sections;
  const blockRules = layout.block_rules ?? {};
  const evalCtx = { ...DEFAULT_PREVIEW_EVAL_CTX, device: viewport };
  const isHiddenOnDevice = (b: BlockId) =>
    blockRules[b]?.hide_on_device?.includes(viewport) === true;
  const isHiddenByRules = (b: BlockId) =>
    !evaluateVisibility(blockRules[b]?.visibility_rules, evalCtx);
  const visibleBlocks = (blocks: BlockId[]) =>
    blocks.filter((b) => !isHiddenOnDevice(b) && !isHiddenByRules(b));

  const renderSelectableBlock = (b: BlockId) => {
    const rendered = renderBlock(b, ctxMerged, node, walletPreviewMode);
    if (rendered == null) return null;
    const isSelected = selection?.block === b;
    if (!onSelectBlock) return <div key={b}>{rendered}</div>;
    return (
      <div
        key={b}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onSelectBlock(b);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelectBlock(b);
          }
        }}
        className={
          "cp-preview-selectable" + (isSelected ? " cp-preview-selectable-selected" : "")
        }
      >
        {rendered}
      </div>
    );
  };

  // Apply per-checkout color overrides as inline CSS variables on the wrapper —
  // matches what theme.ts does for the live page, scoped to the preview.
  const overrides = node.theme_overrides;
  const styleVars: CSSProperties = {};
  const setVar = (k: string, v: string) => {
    (styleVars as Record<string, string>)[k] = v;
  };
  if (overrides?.pay_button_color) {
    setVar("--cp-pay-button", overrides.pay_button_color);
    setVar("--cp-pay-button-hover", darkenHex(overrides.pay_button_color, 0.1));
  }
  if (overrides?.pay_button_text_color) {
    setVar("--cp-pay-button-text", overrides.pay_button_text_color);
  }
  if (overrides?.brand_color) {
    setVar("--cp-brand", overrides.brand_color);
    setVar("--cp-brand-hover", darkenHex(overrides.brand_color, 0.1));
  }
  if (overrides?.bg_color) setVar("--cp-bg", overrides.bg_color);
  if (overrides?.summary_bg_color) setVar("--cp-summary-bg", overrides.summary_bg_color);
  if (overrides?.text_color) setVar("--cp-text", overrides.text_color);
  if (overrides?.text_muted_color) setVar("--cp-text-muted", overrides.text_muted_color);
  if (overrides?.border_color) setVar("--cp-border", overrides.border_color);
  if (overrides?.radius_px !== undefined) {
    setVar("--cp-radius", `${overrides.radius_px}px`);
    setVar("--cp-radius-lg", `${overrides.radius_px + 4}px`);
  }
  if (overrides?.font_family) setVar("--cp-font-family", overrides.font_family);

  const mainOrder = resolvePreviewSectionOrder(layout.section_order);
  const [previewStep, setPreviewStep] = useState(0);

  const SECTION_TITLE: Record<SectionName, string> = {
    contact: "Contact",
    shipping: "Shipping",
    offers: "Offers & upsells",
    payment: "Payment",
    legal: "Legal",
    support: "Support",
    summary: "Order summary",
  };

  const renderSectionNode = (s: SectionName) => {
        const sec = sections[s];
        if (!sec?.visible) return null;
        const blocks = visibleBlocks(sec.blocks);
        return (
          <PreviewSection
            key={s}
            name={s}
            title={SECTION_TITLE[s]}
          >
            {blocks.map(renderSelectableBlock)}
          </PreviewSection>
        );
  };

  const stepDefinitions = [
    {
      id: "step-contact",
      title: "Contact",
      sections: ["contact" as SectionName],
    },
    {
      id: "step-shipping",
      title: "Delivery",
      sections: [
        "shipping" as SectionName,
        ...(sections.offers?.visible ? (["offers"] as SectionName[]) : []),
      ],
    },
    {
      id: "step-payment",
      title: "Payment",
      sections: [
        "payment" as SectionName,
        ...(sections.legal?.visible ? (["legal"] as SectionName[]) : []),
        ...(sections.support?.visible ? (["support"] as SectionName[]) : []),
      ],
    },
  ].filter((step) => step.sections.some((s) => sections[s]?.visible));
  const activeStep = Math.min(previewStep, Math.max(0, stepDefinitions.length - 1));

  const main = node.layout === "multi_step" ? (
    <div className="checkout-main">
      <div className="wizard-tabs" aria-label="Checkout steps">
        {stepDefinitions.map((step, idx) => (
          <button
            key={step.id}
            type="button"
            className={idx === activeStep ? "wizard-tab active" : "wizard-tab"}
            onClick={() => setPreviewStep(idx)}
          >
            <span>{idx + 1}</span>
            {step.title}
          </button>
        ))}
      </div>
      {stepDefinitions.map((step, idx) => (
        <fieldset
          key={step.id}
          className={idx === activeStep ? "wizard-step active" : "wizard-step"}
          data-step-id={step.id}
          data-step-index={idx}
        >
          <legend className="wizard-legend">
            Step {idx + 1}/{stepDefinitions.length}
          </legend>
          {step.sections.map(renderSectionNode)}
          <div className="wizard-nav">
            {idx > 0 ? (
              <button type="button" className="wizard-back" onClick={() => setPreviewStep(idx - 1)}>
                Back
              </button>
            ) : <span />}
            {idx < stepDefinitions.length - 1 ? (
              <button type="button" className="wizard-next" onClick={() => setPreviewStep(idx + 1)}>
                Next
              </button>
            ) : null}
          </div>
        </fieldset>
      ))}
    </div>
  ) : (
    <div className="checkout-main">
      {mainOrder.map(renderSectionNode)}
    </div>
  );

  const summary =
    sections.summary?.visible ? (
      <aside className="sidebar">
        <section className="section" data-section="summary">
          <h2 className="section-title">Order summary</h2>
          <div className="section-body">
            {visibleBlocks(sections.summary.blocks).map(renderSelectableBlock)}
          </div>
        </section>
      </aside>
    ) : null;

  // Store name for the top bar. For MVP we use the flow name; a future
  // per-checkout `brand_name` override or an account-level setting can
  // land here. "Checkout" on the right mirrors Shopify's own checkout
  // header copy.
  const storeName = "Checkout";
  const logoUrl = overrides?.logo_url;

  const validShopDomain =
    shopifyShopDomain && /^[a-z0-9-]+\.myshopify\.com$/.test(shopifyShopDomain.toLowerCase())
      ? shopifyShopDomain.toLowerCase()
      : null;

  return (
    <div className="cp-preview-frame" style={styleVars}>
      <div className="checkout-topbar">
        <div className="checkout-topbar-left">
          {logoUrl ? (
            <img src={logoUrl} alt={storeName} className="store-logo" />
          ) : (
            <span className="store-name">{storeName}</span>
          )}
        </div>
        <div className="checkout-topbar-right">
          {validShopDomain ? (
            <a
              href={`https://${validShopDomain}/cart`}
              className="cart-link"
              title="Edit cart"
              aria-label="Edit cart"
              onClick={(e) => e.preventDefault() /* preview only — don't navigate */}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 7h14l-1.2 11.5a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8L5 7Z" />
                <path d="M9 7V5.5a3 3 0 0 1 6 0V7" />
              </svg>
            </a>
          ) : null}
        </div>
      </div>
      <div
        className={
          "checkout " + (viewport === "desktop" ? "layout-desktop" : "layout-mobile")
        }
      >
        {main}
        {summary}
      </div>
    </div>
  );
}

function PreviewSection({
  name,
  title,
  children,
}: {
  name: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="section" data-section={name}>
      <h2 className="section-title">{title}</h2>
      <div className="section-body">{children}</div>
    </section>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={bold ? "row total" : "row"}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function formatCents(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

const PREVIEW_MAIN_ORDER: SectionName[] = [
  "contact",
  "shipping",
  "offers",
  "payment",
  "legal",
  "support",
];

function resolvePreviewSectionOrder(chosen: SectionName[] | undefined): SectionName[] {
  if (!chosen || chosen.length === 0) return PREVIEW_MAIN_ORDER;
  const seen = new Set<SectionName>();
  const out: SectionName[] = [];
  for (const s of chosen) {
    if (PREVIEW_MAIN_ORDER.includes(s) && !seen.has(s)) {
      out.push(s);
      seen.add(s);
    }
  }
  for (const s of PREVIEW_MAIN_ORDER) {
    if (!seen.has(s)) out.push(s);
  }
  return out;
}

function defaultLayout(): LayoutBlocks {
  return buildDefaultLayoutShape();
}

function buildDefaultLayoutShape(): LayoutBlocks {
  return {
    schema_version: 1,
    sections: {
      contact: { visible: true, blocks: ["email"] },
      shipping: { visible: true, blocks: ["address", "shipping_method"] },
      offers: { visible: false, blocks: [] },
      payment: { visible: true, blocks: ["payment_method_selector", "pay_button"] },
      legal: { visible: false, blocks: [] },
      support: { visible: false, blocks: [] },
      summary: { visible: true, blocks: ["line_items", "totals"] },
    },
  };
}

function darkenHex(hex: string, amount: number): string {
  const m = hex.replace("#", "").match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex;
  const k = Math.max(0, 1 - amount);
  const ch = (n: number) => Math.round(n * k).toString(16).padStart(2, "0");
  return `#${ch(parseInt(m[1], 16))}${ch(parseInt(m[2], 16))}${ch(parseInt(m[3], 16))}`;
}
